import { Fragment, createElement, type ReactNode } from 'react';
import sv from './sv';
import en from './en';

// ─────────────────────────────────────────────────────────────────
// Språkstöd. All gränssnittstext ligger i sv.ts / en.ts — aldrig direkt i
// komponenterna. Annonser och annan text som användarna själva skriver
// översätts inte.
//
//  t('sellerBadge.soldBy')                     → "Säljs av"
//  t('reputation.reviews', { count: 3 })       → "3 betyg" (one/other-former)
//  tn('cart.terms', { link: <a>…</a> })        → ReactNode med inbäddade element
//  vl('category', item.category)               → visningsnamn för ett datavärde
//
// Språket väljs vid sidladdning (localStorage, annars svenska) och byts med
// setLang(), som laddar om sidan — då räknas även modulnivåkonstanter med
// etiketter (statuslistor m.m.) om på rätt språk.
// ─────────────────────────────────────────────────────────────────

export type Lang = 'sv' | 'en';
export const LANGS: { code: Lang; label: string }[] = [
  { code: 'sv', label: 'Svenska' },
  { code: 'en', label: 'English' },
];

type Plural = { one: string; other: string };
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : T[K] extends Plural ? Plural : Shape<T[K]> };
/** Strukturen som varje språkfil måste följa (härledd från sv.ts). */
export type Messages = Shape<typeof sv>;

type Paths<T> = {
  [K in keyof T & string]: T[K] extends string | Plural ? K : `${K}.${Paths<T[K]>}`;
}[keyof T & string];
export type MessageKey = Paths<typeof sv>;

const STORAGE_KEY = 'qvestium_lang';
const DICTS: Record<Lang, Messages> = { sv, en };
const LOCALES: Record<Lang, string> = { sv: 'sv-SE', en: 'en-GB' };

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'sv' || stored === 'en') return stored;
  } catch { /* privat läge m.m. */ }
  return 'sv';
}

const current: Lang = detectLang();
if (typeof document !== 'undefined') document.documentElement.lang = current;

export function getLang(): Lang {
  return current;
}

/** Byter språk och laddar om sidan. */
export function setLang(lang: Lang): void {
  if (lang === current) return;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignorera */ }
  window.location.reload();
}

/** BCP 47-locale för datum och tal, t.ex. toLocaleDateString(locale()). */
export function locale(): string {
  return LOCALES[current];
}

export type Vars = Record<string, string | number | null | undefined>;

function lookup(key: string): string | Plural | undefined {
  const get = (dict: unknown) => key.split('.').reduce<any>((o, k) => (o == null ? undefined : o[k]), dict);
  return get(DICTS[current]) ?? get(DICTS.sv);
}

const pluralRules = new Intl.PluralRules(LOCALES[current]);

function resolve(key: string, vars?: Record<string, unknown>): string {
  const entry = lookup(key);
  if (entry === undefined) {
    if ((import.meta as any).env?.DEV) console.warn(`[i18n] saknad nyckel: ${key}`);
    return key;
  }
  if (typeof entry === 'string') return entry;
  const count = Number(vars?.count ?? 0);
  return pluralRules.select(count) === 'one' ? entry.one : entry.other;
}

/** Översatt text med {variabler} ersatta. */
export function t(key: MessageKey, vars?: Vars): string {
  const text = resolve(key, vars);
  return vars ? text.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name] ?? '') : m)) : text;
}

/** Som t() men variablerna får vara React-element (länkar, fetstil). */
export function tn(key: MessageKey, vars: Record<string, ReactNode>): ReactNode {
  const parts = resolve(key, vars).split(/(\{\w+\})/g);
  return createElement(Fragment, null, ...parts.map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    const node = m && m[1] in vars ? vars[m[1]] : part;
    return createElement(Fragment, { key: i }, node);
  }));
}

/** Visningsnamn för ett datavärde (kategori, stil …) — själva värdet sparas oförändrat. */
export function vl(group: keyof Messages['values'], value: string | null | undefined): string {
  if (!value) return '';
  const entry = (DICTS[current].values[group] as Record<string, string>)[value]
    ?? (DICTS.sv.values[group] as Record<string, string>)[value];
  return entry ?? value;
}

if (typeof document !== 'undefined') document.title = t('meta.title');
